import React, { Component } from 'react';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';

// AdminMerchantApprovals — salon OWNERS awaiting Beu approval to add a payout
// bank account / be paid out. New owners sign up as 'pending'; an owner who
// requests a bank change while unapproved shows a "bank requested" flag. SE
// inherit their owner's status, so only owners appear here. Approving an owner
// unblocks add-bank + payouts for them AND their self-employed staff.

const STATUS_STYLE = {
  pending: { bg: '#FEF0C7', fg: '#B54708', label: 'Pending review' },
  flagged: { bg: '#FEF3F2', fg: '#b42318', label: 'Flagged' },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#F2F4F7', fg: '#475467', label: status || '—' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, background: s.bg, color: s.fg }}>
      {s.label}
    </span>
  );
}

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB') : '—');

class AdminMerchantApprovals extends Component {
  state = {
    rows: [],
    loading: true,
    error: null,
    approvingId: null,
    filter: '', // '' | 'pending' | 'flagged'
  };

  componentDidMount() {
    this.fetchApprovals();
  }

  fetchApprovals = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await adminService.getMerchantApprovals(
        this.state.filter ? { status: this.state.filter } : {}
      );
      const rows = (res && res.responseData) || [];
      this.setState({ rows: Array.isArray(rows) ? rows : [], loading: false });
    } catch (e) {
      console.error('Error fetching merchant approvals:', e);
      this.setState({ loading: false, error: 'Failed to load merchant approvals' });
    }
  };

  setFilter = (filter) => {
    this.setState({ filter }, this.fetchApprovals);
  };

  approve = async (row) => {
    const name = (row.owner && row.owner.name) || (row.salon && row.salon.salonName) || 'this owner';
    if (!window.confirm(`Approve ${name}? This unblocks their payout bank account and payouts (and their self-employed staff).`)) {
      return;
    }
    this.setState({ approvingId: row.hairdresserId, error: null });
    try {
      await adminService.approveMerchant(row.hairdresserId);
      // Drop the approved row locally, then refetch to stay in sync.
      this.setState((prev) => ({
        rows: prev.rows.filter((r) => r.hairdresserId !== row.hairdresserId),
        approvingId: null,
      }));
      this.fetchApprovals();
    } catch (e) {
      console.error('Error approving merchant:', e);
      this.setState({ approvingId: null, error: 'Failed to approve — please try again' });
    }
  };

  render() {
    const { rows, loading, error, approvingId, filter } = this.state;

    return (
      <AdminWrapper title="Merchant Approvals">
        <div className="admin-page-header">
          <div>
            <h1 className="admin-page-title">Merchant Approvals</h1>
            <p className="admin-page-subtitle">
              Salon owners awaiting approval before they can add a payout bank account or be paid
              out. Approving an owner also clears their self-employed staff. Empty is good news.
            </p>
          </div>
        </div>

        <div className="admin-filters" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['', 'All'], ['pending', 'Pending'], ['flagged', 'Flagged']].map(([val, label]) => (
            <button
              key={val || 'all'}
              type="button"
              onClick={() => this.setFilter(val)}
              style={{
                padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: '1px solid ' + (filter === val ? '#29282D' : '#e0e0e0'),
                background: filter === val ? '#29282D' : '#fff',
                color: filter === val ? '#fff' : '#444',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {error && <div style={{ color: '#b42318', marginBottom: 12 }}>{error}</div>}

        {loading && rows.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#667085', background: '#fafafa', borderRadius: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}><span role="img" aria-label="All clear">✅</span></div>
            <strong>No owners awaiting approval{filter ? ` (${filter})` : ''}</strong>
            <div style={{ fontSize: 13, marginTop: 6 }}>New salon-owner signups will appear here.</div>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Salon</th>
                  <th>Owner</th>
                  <th>Status</th>
                  <th>Bank requested</th>
                  <th>Signed up</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.hairdresserId}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{(r.salon && r.salon.salonName) || '—'}</div>
                      <div style={{ fontSize: 12, color: '#667085' }}>{(r.salon && r.salon.formattedAddress) || ''}</div>
                    </td>
                    <td>
                      <div>{(r.owner && r.owner.name) || '—'}</div>
                      <div style={{ fontSize: 12, color: '#667085' }}>{(r.owner && r.owner.email) || ''}</div>
                    </td>
                    <td><StatusBadge status={r.merchantStatus} /></td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.bankRequestedAt
                        ? <span style={{ color: '#B54708', fontWeight: 600 }}>{fmtDate(r.bankRequestedAt)}</span>
                        : <span style={{ color: '#98A2B3' }}>—</span>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(r.signedUpAt)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        disabled={approvingId === r.hairdresserId}
                        onClick={() => this.approve(r)}
                        style={{
                          padding: '7px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                          border: 'none', cursor: approvingId === r.hairdresserId ? 'default' : 'pointer',
                          background: approvingId === r.hairdresserId ? '#98A2B3' : '#067647', color: '#fff',
                        }}
                      >
                        {approvingId === r.hairdresserId ? 'Approving…' : 'Approve'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminWrapper>
    );
  }
}

export default AdminMerchantApprovals;
